import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send, Linkedin } from 'lucide-react';
import './ContactPage.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // For now, just show success message (no backend integration yet)
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', role: '', message: '' });
    }, 3000);
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="page-hero contact-hero" data-testid="contact-hero">
        <div className="page-hero-content">
          <Mail size={64} color="white" />
          <h1>Get in Touch</h1>
          <p>Join us in shaping the next generation of changemakers</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section">
        <div className="contact-wrapper">
          {/* Contact Information */}
          <div className="contact-info">
            <h2>Connect With Us</h2>
            <p className="contact-intro">
              Whether you're a student interested in joining a Lab, a university exploring
              partnership, or a mentor wanting to contribute – we'd love to hear from you.
            </p>

            <div className="contact-methods">
              <div className="contact-method" data-testid="contact-email">
                <Mail size={24} color="var(--primary)" />
                <div>
                  <h4>Email</h4>
                  <a href="mailto:info@litvill.com">info@litvill.com</a>
                </div>
              </div>

              <div className="contact-method" data-testid="contact-location">
                <MapPin size={24} color="var(--primary)" />
                <div>
                  <h4>Headquarters</h4>
                  <p>Global Network - Partner Universities Worldwide</p>
                </div>
              </div>

              <div className="contact-method" data-testid="contact-linkedin">
                <Linkedin size={24} color="var(--primary)" />
                <div>
                  <h4>Connect Online</h4>
                  <a href="https://www.linkedin.com/company/litvill-lessons-association/?viewAsMember=true" target="_blank" rel="noopener noreferrer">
                    Follow us on LinkedIn for updates and opportunities
                  </a>
                </div>
              </div>
            </div>

            <div className="founder-info">
              <h3>Meet the Team</h3>
              <p>
                Litvill Lessons is led by an international team of educators, sustainability advocates, 
                and social entrepreneurs dedicated to supporting youth worldwide.
              </p>
              <p className="founder-note">
                We work with a Central Advisory Board and university mentors across our global network 
                to ensure quality, ethics, and real impact in all our programs.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form">
              <h3>Send Us a Message</h3>
              
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name"
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">I am a... *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  data-testid="contact-role"
                >
                  <option value="">Select your role</option>
                  <option value="student">Student</option>
                  <option value="mentor">Mentor / Educator</option>
                  <option value="university">University Representative</option>
                  <option value="partner">Potential Partner</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  data-testid="contact-message"
                  placeholder="Tell us how you'd like to get involved or any questions you have..."
                />
              </div>

              <button type="submit" className="btn btn-primary submit-btn" data-testid="contact-submit">
                <Send size={18} />
                Send Message
              </button>

              {submitted && (
                <div className="success-message" data-testid="success-message">
                  Thank you! We'll get back to you soon.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper cta-boxes">
          <div className="cta-box" data-testid="cta-student">
            <h3>For Students</h3>
            <p>Start or join a Litvill Lab at your university and become part of a global movement</p>
          </div>
          <div className="cta-box" data-testid="cta-university">
            <h3>For Universities</h3>
            <p>Partner with Litvill to empower your students with skills for sustainable leadership</p>
          </div>
          <div className="cta-box" data-testid="cta-mentor">
            <h3>For Mentors</h3>
            <p>Share your expertise and guide the next generation of changemakers</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;